/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

// redux imports
import { getEntities } from "../../../../redux/features/Settings/Management/entities";

// components imports
import Loader from "../../../../components/Loader/Index";
import PaginationComponent from "../../../../components/_utils/PaginationComponent";
import CardContainer from "../../../../components/_utils/CommonViewAllCard/CardContainer";
import TextWithIcon from "../../../../components/_utils/CommonViewAllCard/CardSubComponent/TextWithIcon";
import PrimaryButton from "../../../../components/_utils/CommonButtons/PrimaryButton";
import TopNavComponent from "../../../../components/_utils/CommonTopNav/TopNavComponent";
import {
  GlobalConfiguration,
  PaginationConfiguration,
} from "../../../../utils/configurationProvider";
import ICONS from "../../../../assets/Icons";

const EntitiesViewAll = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const latestRequestRef = useRef(GlobalConfiguration.lengthCheckZero);

  const { isLoading } = useSelector((state) => state.entities);

  const dropdownOptions = PaginationConfiguration.paginationOptions;

  // Filters and pagination live in the URL so they survive opening an
  // entity and coming back.
  const [searchParam, setSearchParam] = useSearchParams();
  const nameSearch = searchParam.get("search") || "";
  const panSearch = searchParam.get("pan") || "";
  const entityID = searchParam.get("entity_id") || "";
  const currentPage =
    Number(searchParam.get("pageno")) || GlobalConfiguration.lengthCheckOne;
  const pageSizeParam = Number(searchParam.get("pagesize"));
  const pageSize = dropdownOptions.includes(pageSizeParam)
    ? pageSizeParam
    : dropdownOptions[GlobalConfiguration.lengthCheckZero];

  const [totalCount, setTotalCount] = useState(
    GlobalConfiguration.lengthCheckZero,
  );
  const [entitiesDetails, setEntitiesDetails] = useState([]);
  const [mainData, setMainData] = useState([]);
  const [searchUserText, setSearchUserText] = useState("");
  const [active, setActive] = useState("");
  const [filterOpen, setFilterOpen] = useState(null);
  const [showInnerSearchBar, setShowInnerSearchBar] = useState(false);
  const [fetching, setFetching] = useState(false);

  // "Clear all" calls setValue on every filter in the same click, before the
  // URL re-renders, so each update must build on the previous one.
  const pendingParamsRef = useRef(null);

  useEffect(() => {
    pendingParamsRef.current = null;
  }, [searchParam]);

  const updateSearchParams = (updates) => {
    const params = new URLSearchParams(pendingParamsRef.current ?? searchParam);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === "" || value === null || value === undefined) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });
    pendingParamsRef.current = params;
    setSearchParam(params, { replace: true });
  };

  useEffect(() => {
    const requestId =
      latestRequestRef.current + GlobalConfiguration.lengthCheckOne;
    latestRequestRef.current = requestId;

    const fetchData = async () => {
      setFetching(true);
      setEntitiesDetails([]);
      setMainData([]);
      setTotalCount(GlobalConfiguration.lengthCheckZero);

      try {
        const response = await dispatch(
          getEntities({
            page_no: currentPage,
            page_size: pageSize,
            search_string: nameSearch || panSearch,
            pan: panSearch,
            entity_id: entityID,
          }),
        );

        if (requestId !== latestRequestRef.current) return;

        setMainData(response?.payload || []);
        setTotalCount(
          response?.payload?.total_count || GlobalConfiguration.lengthCheckZero,
        );
        setEntitiesDetails(response?.payload?.entities || []);
      } catch (error) {
        if (requestId !== latestRequestRef.current) return;

        setMainData([]);
        setTotalCount(GlobalConfiguration.lengthCheckZero);
        setEntitiesDetails([]);
      } finally {
        if (requestId === latestRequestRef.current) {
          setFetching(false);
        }
      }
    };

    fetchData();
  }, [nameSearch, panSearch, entityID, pageSize, currentPage]);

  const handleSelect = (value) => {
    updateSearchParams({
      pagesize: value,
      pageno: GlobalConfiguration.lengthCheckOne,
    });
  };

  const handlePagination = (page) => {
    updateSearchParams({ pageno: page });
  };

  const handleClearParams = () => {
    setSearchUserText("");
    pendingParamsRef.current = new URLSearchParams();
    setSearchParam(pendingParamsRef.current, { replace: true });
  };

  return (
    <>
      <div
        className={`bg-primary-bg flex flex-col h-screen relative overflow-y-hidden`}
        style={{
          overflow: filterOpen && "hidden",
        }}
      >
        <div className="Qsmd:min-h-navbar-breadcrumb-height bg-primary-nav"></div>
        <div className="relative z-z-index-dropdown">
          <TopNavComponent
            isFormattingNotRequired={true}
            topNavHeader={
              isLoading || fetching ? (
                <Loader
                  height="var(--icon-md)"
                  width="var(--icon-md)"
                />
              ) : (
                totalCount
              )
            }
            topNavSubText={"Total entities"}
            handleBack={() => {
              if (filterOpen) {
                setFilterOpen(false);
              } else {
                navigate(GlobalConfiguration.navigate_back);
              }
            }}
            filteredSearchProps={{
              active: active,
              setActive: setActive,
              setShowInnerSearchBar: setShowInnerSearchBar,
              showSearchBar: false,
              isSearchDisabled: filterOpen,
              onSearchInput: (val) => {
                setSearchUserText(val);
              },
              searchText: searchUserText,
              searchPlaceholder: "Search entities",
              filterPlaceholder: "Name / address",
              filterItems: [
                {
                  name: "Search",
                  short_name: "Search",
                  isMulti: false,
                  value: nameSearch,
                  setValue: (val) => {
                    updateSearchParams({
                      search: val,
                      pageno: GlobalConfiguration.lengthCheckOne,
                    });
                  },
                  isDropdown: false,
                  valueType: "string",
                  isDisabled: false,
                },
                {
                  name: "PAN number",
                  short_name: "PAN",
                  isMulti: false,
                  value: panSearch,
                  setValue: (val) => {
                    updateSearchParams({
                      pan: val,
                      pageno: GlobalConfiguration.lengthCheckOne,
                    });
                  },
                  isDropdown: false,
                  valueType: "string",
                  isDisabled: false,
                },
                {
                  name: "Entity ID",
                  short_name: "Entity ID",
                  isMulti: false,
                  value: entityID,
                  setValue: (val) => {
                    updateSearchParams({
                      entity_id: val,
                      pageno: GlobalConfiguration.lengthCheckOne,
                    });
                  },
                  isDropdown: false,
                  valueType: "string",
                  isDisabled: false,
                },
              ],
              setIsFilterOpen: setFilterOpen,
              setIsSortOpen: () => {},
              loading: isLoading,
              isSortDisabled: isLoading,
              isFilterDisabled: isLoading,
            }}
          />
        </div>
        {showInnerSearchBar && (
          <div className="absolute top-[var(--navbar-breadcrumb-height)] left-left-base right-right-base bottom-bottom-base bg-PopUp-BackDrop opacity-light-opacity z-z-index-overlay" />
        )}
        {isLoading || fetching ? (
          <>
            <div className="loader-container m-auto justify-center items-center flex loader-height">
              <Loader />
            </div>
          </>
        ) : entitiesDetails.length !== GlobalConfiguration.lengthCheckZero ? (
          <>
            <div
              className={
                showInnerSearchBar
                  ? "overflow-y-hidden h-screen entitiesviewallscroll relative z-z-index-base"
                  : `overflow-y-auto h-screen mb-margin-xl entitiesviewallscroll pb-padding-button-m ${showInnerSearchBar ? "mt-margin-xl Qsmd:mt-margin-base" : ""}`
              }
            >
              <div
                className={`${
                  showInnerSearchBar && "pointer-events-none "
                } standard-card-alignment gap-y-gap-md gap-x-gap-sm`}
              >
                {entitiesDetails.map(
                  ({ entity_id, entity_name, entity_pan }) => {
                    return (
                      <CardContainer
                        key={entity_id}
                        onClickHandler={(e, c) => {
                          const isViewAll = location.pathname.includes(
                            `settings/entities_view_all`,
                          );

                          navigate(`entities_detail_page/${entity_id}`, {
                            state: { isViewAll },
                          });
                        }}
                      >
                        <card-body>
                          <TextWithIcon
                            type={"text"}
                            fontWeight={"semibold"}
                            text={entity_name}
                            textColor={"var(--text-primary)"}
                          />
                          <TextWithIcon
                            type={"text"}
                            fontWeight={"normal"}
                            subText={"PAN number"}
                            text={entity_pan}
                            textColor={"var(--text-primary)"}
                          />
                        </card-body>
                      </CardContainer>
                    );
                  },
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              className={
                filterOpen
                  ? "h-height-full w-width-full flex flex-col items-center justify-center m-auto pointer-events-none"
                  : "flex flex-col items-center justify-center m-auto"
              }
            >
              <img
                src={ICONS?.NO_DATA_FOUND}
                alt="No accounts found"
                className="w-graphic-width-md h-graphic-rectangle-height-md"
                style={{
                  opacity: filterOpen
                    ? "var(--light-opacity)"
                    : "var(--opacity-base)",
                }}
              />
              <p
                className={
                  filterOpen
                    ? "no-owneraccount-text-opacity"
                    : "text-font-sm opacity-base-opacity py-padding-sm text-text-primary"
                }
              >
                No data found.
              </p>
              <PrimaryButton
                handleFunction={handleClearParams}
                enabledTextColor="var(--text-primary)"
                enabledBgColor="var(--primary-nav-act)"
                btnText="Clear parameters"
                hoverOption="white"
                btnType="button"
              />
            </div>
          </>
        )}
        {entitiesDetails.length !== GlobalConfiguration.lengthCheckZero && (
          <>
            <div
              style={{
                zIndex: showInnerSearchBar
                  ? "var(--z-index-content)"
                  : "var(--z-index-max)",
                width: "var(--width-full)",
                bottom: "var(--bottom-base)",
              }}
              className="fixed w-full"
            >
              <PaginationComponent
                handleSelect={handleSelect}
                selectedValue={pageSize}
                data={mainData}
                pageSize={pageSize}
                dropdownOptions={dropdownOptions}
                handlePagination={handlePagination}
                currentPage={currentPage}
                setIsOpen={(e) => {}}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default EntitiesViewAll;
